import { cloneDeep, isEqual } from 'lodash';
import { actionTypes, SUGGESTED_LIMIT_TYPES } from './constants';
import { updateLimitReducerData, updateSuggestedLimitReducerData, updateSelectedTimeSeriesInListReducerData, mergeSuggestedLimits, handleUnitConversionSuggestedLimits, handleDiscardLimits, updateDefaultLimitsToCurrentLimits } from './helper';
import { apiCallStatus } from 'helpers/constants';

const initialState = {
    limitKPI: {
        kpiList: [],
        error: null,
        isLoading: null
    },
    currentTimeSeries: [],
    timeSeries: {
        timeSeriesList: [],
        error: null,
        isLoading: null,
        assetId: null
    },
    showSuggestLimitModal: false,
    selectedTimeSeriesForSuggestion: null,
    suggestedLimit: {
        data: null,
        isLoading: false,
        limits: []
    },
    expandedSignals: [],
    defaultLimit: {
        data: null,
        isLoading: false
    },
    offsetModalId: null,
};

const handleGetTimeSeries = (state) => ({
    ...state,
    timeSeries: {
        ...state.timeSeries,
        isLoading: apiCallStatus.LOADING,
    },
});
const handleGetTimeSeriesSuccess = (state, action) => {
    const { temp, assetId } = action.payload;
    return {
        ...state,
        timeSeries: {
            timeSeriesList: [...temp],
            isLoading: apiCallStatus.SUCCESS,
            error: null,
            assetId: assetId,
        },
        currentTimeSeries: [...temp]
    };
};
const handleGetTimeSeriesFailure = (state, action) => ({
    ...state,
    timeSeries: {
        timeSeriesList: [],
        isLoading: apiCallStatus.ERROR,
        error: action.error,
        statusCode: action.statusCode,
    },
});
const handleGetLimitKpi = (state, action) => {
    switch (action.type) {
        case actionTypes.GET_LIMIT_KPI_SUCCESS:
            return {
                ...state,
                limitKPI: {
                    kpiList: action.payload,
                    error: null,
                    isLoading: apiCallStatus.SUCCESS
                }
            };
        case actionTypes.GET_LIMIT_KPI_FAILURE:
            return {
                ...state,
                limitKPI: {
                    ...state.limitKPI,
                    error: action.error,
                    statusCode: action.statusCode,
                    isLoading: apiCallStatus.ERROR
                }
            };
        default:
            return state;
    }
};
const handleAddSignal = (state, action) => {
    const signalExists = state.currentTimeSeries.some(
        item => item.displayName === action.payload.displayName
    );
    // to show error if signal is not selected and trying to add more signals
    if (signalExists) {
        return {
            ...state,
            currentTimeSeries: state.currentTimeSeries.map(item => !item.displayName
                ? { ...item, isSignalError: true }
                : item
            )
        };
    }
    return {
        ...state,
        currentTimeSeries: [action.payload, ...state.currentTimeSeries]
    };
};
const handleUpdateSignal = (state, action) => {
    const { uniqId, field, value } = action.payload;
    return {
        ...state,
        currentTimeSeries: state.currentTimeSeries?.map(item =>
            item.uniqId === uniqId ? { ...item, [field]: value } : item
        )
    };
};
const handleLimitTypeChange = (state, action) => {
    const { updatedItem } = action.payload;
    const updatedTimeSeries = state.currentTimeSeries.map(item =>
        item.uniqId === updatedItem.uniqId ? updatedItem : item
    );
    return {
        ...state,
        currentTimeSeries: updatedTimeSeries,
    };
};
const handleDeleteLimit = (state, action) => {
    const { item } = action.payload;
    // Find the currentTimeSeries item to update
    const currentTimeSeriesItemIndex = state.currentTimeSeries.findIndex(currentItem =>
        currentItem.limits.some(limit => limit.uniqId === item.uniqId)
    );
    const updatedCurrentTimeSeries = {
        ...state.currentTimeSeries[currentTimeSeriesItemIndex],
        limits: state.currentTimeSeries[currentTimeSeriesItemIndex].limits.filter(
            limit => limit.uniqId !== item.uniqId
        )
    };
    // Find the corresponding item in timeSeriesList for comparison
    const correspondingTimeSeriesItem = state.timeSeries.timeSeriesList.find(item =>
        item.uniqId === state.currentTimeSeries[currentTimeSeriesItemIndex].uniqId
    );
    if (correspondingTimeSeriesItem !== undefined) {
        // Update the currentTimeSeries item by removing the limit
        const isModified = !isEqual(updatedCurrentTimeSeries.limits, correspondingTimeSeriesItem.limits);
        // Update isModified in updatedCurrentTimeSeries
        updatedCurrentTimeSeries.isModified = isModified;
    } else {
        updatedCurrentTimeSeries.isModified = true;
    }
    // Update state with updated currentTimeSeries item
    return {
        ...state,
        currentTimeSeries: [
            ...state.currentTimeSeries.slice(0, currentTimeSeriesItemIndex),
            updatedCurrentTimeSeries,
            ...state.currentTimeSeries.slice(currentTimeSeriesItemIndex + 1)
        ]
    };
};
const handleUpdateLimit = (state, action) => {
    const { updatedTimeSeries, currentTimeSeriesItemIndex } = updateLimitReducerData(state, action);
    // Update state with updated currentTimeSeries item
    return {
        ...state,
        currentTimeSeries: [
            ...state.currentTimeSeries.slice(0, currentTimeSeriesItemIndex),
            updatedTimeSeries,
            ...state.currentTimeSeries.slice(currentTimeSeriesItemIndex + 1)
        ]
    };
};
const handleAddLimit = (state, action) => {
    const { currentTimeSeriesUniqId, limit } = action.payload;
    const currentTimeSeriesItemIndex = state.currentTimeSeries.findIndex(item => item.uniqId === currentTimeSeriesUniqId);
    const correspondingTimeSeriesItem = state.timeSeries.timeSeriesList.find(item => item.uniqId === currentTimeSeriesUniqId);
    const updatedCurrentTimeSeries = {
        ...state.currentTimeSeries[currentTimeSeriesItemIndex],
        limits: [...state.currentTimeSeries[currentTimeSeriesItemIndex].limits, { ...limit, isModified: true }]
    };
    if (correspondingTimeSeriesItem !== undefined) {
        const isModified = !isEqual(updatedCurrentTimeSeries.limits, correspondingTimeSeriesItem.limits);
        updatedCurrentTimeSeries.isModified = isModified;
    } else {
        updatedCurrentTimeSeries.isModified = true;
    }
    return {
        ...state,
        currentTimeSeries: [
            ...state.currentTimeSeries.slice(0, currentTimeSeriesItemIndex),
            updatedCurrentTimeSeries,
            ...state.currentTimeSeries.slice(currentTimeSeriesItemIndex + 1)
        ]
    };
};
const handleDiscardLimit = (state, action) => {
    const { currentTimeSeriesUniqId } = action.payload;
    const updatedCurrentTimeSeries = handleDiscardLimits(state, action.payload);
    // Find the index of the currentTimeSeries item to update
    const currentTimeSeriesItemIndex = state.currentTimeSeries.findIndex(item => item.uniqId === currentTimeSeriesUniqId);
    if (currentTimeSeriesItemIndex !== -1) {
        // Update state with the updated currentTimeSeries item
        return {
            ...state,
            currentTimeSeries: [
                ...state.currentTimeSeries.slice(0, currentTimeSeriesItemIndex),
                updatedCurrentTimeSeries,
                ...state.currentTimeSeries.slice(currentTimeSeriesItemIndex + 1)
            ]
        };
    } else {
        // Handle the case where currentTimeSeriesItem is not found
        return state;
    }
};
const handleRemoveSignal = (state, action) => {
    const { currentTimeSeriesUniqId } = action.payload;
    return {
        ...state,
        currentTimeSeries: state.currentTimeSeries.filter(
            (item) => item.uniqId !== currentTimeSeriesUniqId
        ),
    };
};
const handleCancel = (state) => {
    // Create a new currentTimeSeries array based on timeSeriesList
    const newCurrentTimeSeries = state.timeSeries?.timeSeriesList;
    // Update the state with the new currentTimeSeries array
    return {
        ...state,
        currentTimeSeries: newCurrentTimeSeries
    };
};
const handlePostLimit = (state, action) => {
    switch (action.type) {
        case actionTypes.POST_LIMIT_REQUEST:
            return {
                ...state,
                timeSeries: {
                    ...state.timeSeries,
                    error: null,
                    suppressGlobalNotification: false,
                    isLoading: apiCallStatus.LOADING,
                }
            };
        case actionTypes.POST_LIMIT_SUCCESS:
            return {
                ...state,
                timeSeries: {
                    ...state.timeSeries,
                    isLoading: apiCallStatus.SUCCESS,
                    error: null,
                    suppressGlobalNotification: false
                }
            };
        case actionTypes.POST_LIMIT_FAILURE:
            return {
                ...state,
                timeSeries: {
                    ...state.timeSeries,
                    isLoading: apiCallStatus.ERROR,
                    error: action.error,
                    statusCode: action.statusCode,
                    suppressGlobalNotification: action.suppressGlobalNotification,
                }
            };
        default:
            return state;
    }
};
const handleSignalUpdate = (state, action) => {
    const updatedItem = action.payload;
    const updatedItemIndex = state.currentTimeSeries.findIndex(item => item.uniqId === updatedItem.uniqId);
    if (updatedItemIndex !== undefined) {
        // Create a new array with the updated item
        const updatedCurrentTimeSeries = [
            ...state.currentTimeSeries.slice(0, updatedItemIndex),
            updatedItem,
            ...state.currentTimeSeries.slice(updatedItemIndex + 1)
        ];
        return {
            ...state,
            currentTimeSeries: updatedCurrentTimeSeries
        };
    }
    // If the item is not found, return the state unchanged
    return state;
};
const handleSuggestLimitModal = (state, action) => ({
    ...state,
    showSuggestLimitModal: action.show
});
const handleSelectedTimeSeriesForSuggestion = (state, action) => ({
    ...state,
    selectedTimeSeriesForSuggestion: action.payload
});
const handleSuggestedLimitData = (state, action) => {
    switch (action.type) {
        case actionTypes.GET_SUGGESTED_LIMIT_DATA:
            return {
                ...state,
                suggestedLimit: {
                    data: null,
                    isLoading: true,
                    limits: []
                }
            };
        case actionTypes.GET_SUGGESTED_LIMIT_DATA_FAILURE:
            return {
                ...state,
                suggestedLimit: {
                    ...state.suggestedLimit,
                    isLoading: apiCallStatus.ERROR,
                    error: action.error,
                    statusCode: action.statusCode,
                }
            };
        case actionTypes.SET_SUGGESTED_LIMIT_DATA: {
            const suggestedLimits = mergeSuggestedLimits(action.payload);
            let limitsClone = cloneDeep(suggestedLimits);
            let updatedTimeSeries = state.selectedTimeSeriesForSuggestion;
            const customLimits = state.selectedTimeSeriesForSuggestion?.limits?.filter((lmt) => !lmt.isUserLimit);
            const personalLimits = state.selectedTimeSeriesForSuggestion?.limits?.filter((lmt) => lmt.isUserLimit);
            if (suggestedLimits?.length) {
                const updatedLimits = customLimits?.map((msl) => {
                    const limit = suggestedLimits?.find((lmt) => lmt.limitLevel === msl.limitLevel && lmt.limitDirection === msl.limitDirection);
                    limitsClone = limitsClone?.filter((lmt) => lmt.uniqId !== limit?.uniqId);
                    return { ...msl, hasCurrentLimit: true, };
                });
                limitsClone?.map((sl) => {
                    updatedLimits.push({ ...sl, hasCurrentLimit: false });
                });
                // SHORT LIMITS IN ORDER TO 'UpperLimitAlarm', 'UpperLimitAlert', 'LowerLimitAlert', 'LowerLimitAlarm'
                const sortedLimits = updatedLimits?.sort((a, b) => SUGGESTED_LIMIT_TYPES.indexOf(`${a.limitDirection}${a.limitLevel}`) - SUGGESTED_LIMIT_TYPES.indexOf(`${b.limitDirection}${b.limitLevel}`));
                updatedTimeSeries = {
                    ...state.selectedTimeSeriesForSuggestion,
                    limits: [...sortedLimits, ...personalLimits]
                };
            }
            const { convertedLimits } = handleUnitConversionSuggestedLimits(updatedTimeSeries, suggestedLimits);
            return {
                ...state,
                selectedTimeSeriesForSuggestion: updatedTimeSeries,
                suggestedLimit: {
                    data: action.payload,
                    isLoading: false,
                    limits: convertedLimits
                }
            };
        }
        default:
            return state;
    }
};
const handleUpdateSelectedSuggestedSignal = (state, action) => {
    const { updatedTimeSeries } = updateSuggestedLimitReducerData(state, action);
    return {
        ...state,
        selectedTimeSeriesForSuggestion: updatedTimeSeries
    };
};
const handleUpdateLimitSelectedTimeSeries = (state, action) => {
    const { updatedTimeSeriesList } = updateSelectedTimeSeriesInListReducerData(state, action);
    return {
        ...state,
        currentTimeSeries: updatedTimeSeriesList
    };
};
const handleSetExpandedSignals = (state, action) => ({
    ...state,
    expandedSignals: action.payload
});
const handleDefaultLimits = (state, action) => {
    switch (action.type) {
        case actionTypes.GET_DEFAULT_LIMITS:
            return {
                ...state,
                defaultLimit: {
                    data: null,
                    isLoading: apiCallStatus.LOADING
                }
            };
        case actionTypes.SET_DEFAULT_LIMIT_DATA:
            return {
                ...state,
                defaultLimit: {
                    data: action.payload,
                    isLoading: apiCallStatus.SUCCESS
                }
            };
        case actionTypes.GET_DEFAULT_LIMITS_FAILURE:
            return {
                ...state,
                defaultLimit: {
                    data: null,
                    isLoading: apiCallStatus.ERROR,
                    error: action.error,
                    statusCode: action.statusCode,
                }
            };
        default:
            return state;
    }
};
const handleUpdateDefaultLimits = (state, action) => {
    const { updatedTimeSeriesList } = updateDefaultLimitsToCurrentLimits(state, action);
    return {
        ...state,
        currentTimeSeries: updatedTimeSeriesList
    };
};
const handleResetState = (state) => ({
    ...initialState,
    expandedSignals: state.expandedSignals
});
const handleSetOffsetModalId = (state, action) => ({
    ...state,
    offsetModalId: action.data
});

const reducer = (state = initialState, action) => {
    switch (action.type) {
        case actionTypes.GET_TIME_SERIES:
            return handleGetTimeSeries(state);

        case actionTypes.GET_TIME_SERIES_SUCCESS:
            return handleGetTimeSeriesSuccess(state, action);

        case actionTypes.GET_TIME_SERIES_FAILURE:
            return handleGetTimeSeriesFailure(state, action);

        case actionTypes.GET_LIMIT_KPI_SUCCESS:
        case actionTypes.GET_LIMIT_KPI_FAILURE:
            return handleGetLimitKpi(state, action);

        case actionTypes.ADD_SIGNAL:
            return handleAddSignal(state, action);

        case actionTypes.UPDATE_SIGNAL:
            return handleUpdateSignal(state, action);

        case actionTypes.LIMIT_TYPE_CHANGE:
            return handleLimitTypeChange(state, action);

        case actionTypes.DELETE_LIMIT:
            return handleDeleteLimit(state, action);

        case actionTypes.UPDATE_LIMIT:
            return handleUpdateLimit(state, action);

        case actionTypes.ADD_LIMIT:
            return handleAddLimit(state, action);

        case actionTypes.DISCARD_LIMIT:
            return handleDiscardLimit(state, action);

        case actionTypes.REMOVE_SIGNAL:
            return handleRemoveSignal(state, action);

        case actionTypes.CANCEL:
            return handleCancel(state);

        case actionTypes.POST_LIMIT_REQUEST:
        case actionTypes.POST_LIMIT_SUCCESS:
        case actionTypes.POST_LIMIT_FAILURE:
            return handlePostLimit(state, action);

        case actionTypes.HANDLE_SIGNAL_UPDATE:
            return handleSignalUpdate(state, action);

        case actionTypes.HANDLE_SUGGEST_LIMIT_MODAL:
            return handleSuggestLimitModal(state, action);

        case actionTypes.SELECTED_TIMESERIES_FOR_SUGGESTION:
            return handleSelectedTimeSeriesForSuggestion(state, action);

        case actionTypes.GET_SUGGESTED_LIMIT_DATA:
        case actionTypes.SET_SUGGESTED_LIMIT_DATA:
        case actionTypes.GET_SUGGESTED_LIMIT_DATA_FAILURE:
            return handleSuggestedLimitData(state, action);

        case actionTypes.UPDATE_SELECTED_SUGGESTED_SIGNAL:
            return handleUpdateSelectedSuggestedSignal(state, action);

        case actionTypes.UPDATE_LIMIT_SELECTED_TIMESERIES:
            return handleUpdateLimitSelectedTimeSeries(state, action);

        case actionTypes.SET_EXPANDED_SIGNALS:
            return handleSetExpandedSignals(state, action);

        case actionTypes.GET_DEFAULT_LIMITS:
        case actionTypes.SET_DEFAULT_LIMIT_DATA:
        case actionTypes.GET_DEFAULT_LIMITS_FAILURE:
            return handleDefaultLimits(state, action);

        case actionTypes.UPDATE_DEFAULT_LIMITS:
            return handleUpdateDefaultLimits(state, action);

        case actionTypes.RESET_STATE:
            return handleResetState(state);

        case actionTypes.SET_OFFSET_MODAL_ID:
            return handleSetOffsetModalId(state, action);

        default:
            return state;
    }
};

export default reducer;
